import * as Sentry from '@sentry/react'
import { getCurrentUserId } from '../supabase/client'
import { getCloudSyncState, getCloudTags, getCloudProjects } from '../supabase/queries'
import { db, withSyncWrite } from '../db/indexeddb'
import { getPendingChanges, markAsFailed, clearSyncedItems } from './sync-queue'
import { markSyncRun } from './lastSynced'
import { pullRemoteEntities } from './sync-pull'
import { pushEntityChange } from './sync-push'
import type { PushDescriptor } from './sync-push'
import { taskPullDescriptor, taskPushDescriptor, projectPullDescriptor, projectPushDescriptor, sectionPullDescriptor, sectionPushDescriptor, tagPullDescriptor, tagPushDescriptor, goalPullDescriptor, goalPushDescriptor, habitPullDescriptor, habitPushDescriptor, habitLogPullDescriptor, habitLogPushDescriptor, subtaskPullDescriptor, subtaskPushDescriptor, timeEntryPullDescriptor, timeEntryPushDescriptor, taskCommentPullDescriptor, taskCommentPushDescriptor, taskReminderPullDescriptor, taskReminderPushDescriptor, projectCommentPullDescriptor, projectCommentPushDescriptor, rewardUsagePullDescriptor, rewardUsagePushDescriptor, userPreferencePullDescriptor, userPreferencePushDescriptor, canvasNotePullDescriptor, canvasNotePushDescriptor } from './sync-entity-configs'
import { repairOrphanedTaskAssociations, purgeSyncedDeletions, purgeOrphanedCloudTags, stageOrphanedLocalRecords } from './sync-repair'
import { remapMergedProject } from './mergeProjectReferences'
import { detectForeignLocalData } from './ownership-guard'
import { convertLegacySubtasks } from '../db/legacySubtasks'
import { wipeUserLocalState } from '../auth/wipeLocalState'
import type { SyncResult, SyncOptions } from './types'
import { createEmptySyncResult, SYNC_IN_PROGRESS } from './types'
import type { SyncQueueItem } from '../db/models'
import { trackEvent } from '../analytics'
import { ANALYTICS_EVENTS } from '../analytics/events'
import { getPlatformAPI } from '../platform'
import { setSyncNotifier } from './sync-notify'

/**
 * Bidirectional Sync Engine
 * Orchestrates push/pull synchronization between local Dexie and Supabase cloud.
 */

/** Prevents concurrent sync operations */
let isSyncing = false

/** Debounced sync timer */
let syncTimer: ReturnType<typeof setTimeout> | null = null
let syncMaxTimer: ReturnType<typeof setTimeout> | null = null

const SYNC_DEBOUNCE_MS = 1_000
const SYNC_MAX_WAIT_MS = 5_000
/** While the user has interacted within the last `USER_QUIET_MS`, scheduled
 *  syncs slip — push/pull cycles serialise on Dexie + the main thread, and
 *  a sync that lands during a click chain stalls the next user action by
 *  hundreds of ms. SYNC_MAX_WAIT_MS still forces an upper bound (5 s). */
const USER_QUIET_MS = 600
/** Abstand, in dem ein abgewiesener Sync es erneut versucht (siehe `runOrRetry`). */
const SYNC_RETRY_MS = 1_000
let lastUserActionAt = 0

/** Offline-first gate for scheduled syncs. Set by SyncProvider from
 *  `isAuthenticated && storageMode === 'cloud-sync'`. While false the app is
 *  in local-only mode — `scheduleSync()` no-ops so mutations never reach the
 *  network. Sync still runs only once it's explicitly activated. */
let isSyncActive = false

/** Enable/disable scheduled syncs (offline-first gate). */
export function setSyncActive(active: boolean): void {
  isSyncActive = active
}

/** Mark "the user just did something". Mutation handlers call this so
 *  scheduled syncs wait for the user to settle before competing for the
 *  Dexie connection. Cheap — just bumps a timestamp. */
export function markUserAction(): void {
  lastUserActionAt = Date.now()
}

/**
 * Schedule a debounced sync. Multiple calls within SYNC_DEBOUNCE_MS are
 * batched into a single performSync call. If the user is actively
 * interacting (last action within USER_QUIET_MS), the timer reschedules
 * itself instead of firing — so user actions don't stall behind a sync.
 * A sync is forced after SYNC_MAX_WAIT_MS regardless.
 *
 * Every caller of `scheduleSync` is downstream of a user mutation, so
 * we mark a user action implicitly here. That way no caller has to
 * remember to invoke `markUserAction()` separately.
 */
export function scheduleSync(): void {
  // Offline-first: no scheduled sync until HappyCloud-Sync is activated.
  if (!isSyncActive) return
  markUserAction()
  if (syncTimer) clearTimeout(syncTimer)
  const tick = () => {
    const sinceUser = Date.now() - lastUserActionAt
    if (sinceUser < USER_QUIET_MS) {
      // User is still active — slip the timer, but keep the max-wait
      // safety net intact so push/pull eventually fires.
      syncTimer = setTimeout(tick, USER_QUIET_MS - sinceUser)
      return
    }
    syncTimer = null
    if (syncMaxTimer) { clearTimeout(syncMaxTimer); syncMaxTimer = null }
    void runOrRetry()
  }
  syncTimer = setTimeout(tick, SYNC_DEBOUNCE_MS)

  if (!syncMaxTimer) {
    syncMaxTimer = setTimeout(() => {
      if (syncTimer) { clearTimeout(syncTimer); syncTimer = null }
      syncMaxTimer = null
      void runOrRetry()
    }, SYNC_MAX_WAIT_MS)
  }
}

/**
 * **Ein abgewiesener Sync verfällt nicht — er stellt sich neu.**
 *
 * `performSync` liest die Warteschlange EINMAL (`getPendingChanges`), danach
 * laufen elf Pull-Durchgänge. Wer in dieser Zeit etwas ändert, reiht hinter dem
 * Lesekopf ein: Der laufende Zyklus nimmt die Änderung nicht mehr mit. Ohne
 * Nachschlag verfiele die Anmeldung mit „Sync already in progress" — und die
 * Änderung läge bis zum nächsten fremden Anlass, also bis zu fünf Minuten.
 *
 * Das Fenster ist nicht schmal: Es ist die gesamte Pull-Phase, also genau die
 * Zeit, in der der Nutzer weiterarbeitet. Eine Regel, die dann aussetzt, wenn
 * viel passiert, ist keine (ADR 0017).
 *
 * Hier und nicht im `SyncProvider`: Der sieht nur die Läufe, die er selbst
 * auslöst — der Melder aus `queueSyncChange` läuft an ihm vorbei.
 *
 * Dieselbe Mechanik wie das Wegschieben bei aktiver Hand: Zeitgeber neu setzen,
 * `syncMaxTimer` unangetastet lassen. Er ist hier ohne Sicherungsnetz, weil ein
 * laufender Sync IMMER endet und der nächste Versuch dann durchkommt.
 */
async function runOrRetry(): Promise<void> {
  try {
    const result = await performSync()
    if (result.errors.includes(SYNC_IN_PROGRESS)) {
      if (syncTimer) clearTimeout(syncTimer)
      syncTimer = setTimeout(() => {
        syncTimer = null
        void runOrRetry()
      }, SYNC_RETRY_MS)
    }
  } catch {
    /* Ein geworfener Sync ist kein Abweisen — er hat gelaufen und ist gescheitert. */
  }
}

/** Yield once to the event loop so React + Dexie LiveQuery callbacks
 *  scheduled during the prior pull step get a turn before the next
 *  pull starts. Cheap (~ms-scale via macrotask). */
export function yieldToEventLoop(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0))
}

/*
  **Wer einreiht, will hochladen** (ADR 0017).

  Der Melder wird beim Laden dieses Moduls eingehängt: Ab dann stößt jeder
  Eintrag in der Warteschlange den nächsten Sync an, und keine Schreibstelle
  muss `scheduleSync()` von Hand rufen. `scheduleSync` selbst prüft `isSyncActive`
  — im Offline-Modus tut der Melder also nichts.

  Beim Laden und nicht in einem Effekt des `SyncProvider`: `queueSyncChange`
  läuft auch dort, wo kein React-Baum steht (Migrationen, Hintergrundfenster).
*/
setSyncNotifier(scheduleSync)

/** Reset internal state (for tests only) */
export function _resetSyncState(): void {
  isSyncing = false
  isSyncActive = false
  lastUserActionAt = 0
  if (syncTimer) { clearTimeout(syncTimer); syncTimer = null }
  if (syncMaxTimer) { clearTimeout(syncMaxTimer); syncMaxTimer = null }
}

/**
 * Stage local-only records of a natural-key entity (Tags, Projects) for the
 * push, de-duplicating by name against the cloud and against already-synced
 * local records. A same-name duplicate is merged away locally — references are
 * re-pointed at the surviving synced twin (the cloud-only twin is reconciled by
 * the pull + `repairOrphanedTaskAssociations` afterwards), then the duplicate is
 * deleted so its queued create becomes a no-op. A unique record is (re)queued for
 * the normal push.
 *
 * This is the SINGLE place that uploads local-only natural-key records. It runs
 * on every sync, so the first sync after connecting an offline account dedups
 * correctly. It replaces the former `initialSync`, which uploaded tags/tasks
 * directly and bypassed this dedup — creating duplicate tags when connecting to a
 * cloud account that already held a same-name tag.
 */
async function stageUnsyncedWithNameDedup(opts: {
  table: { delete: (key: string) => Promise<unknown> }
  tableName: 'tags' | 'projects'
  unsynced: Array<{ id: string; name: string }>
  cloudNames: Set<string>
  localSynced: Array<{ id: string; name: string }>
  remapReferences: (fromLocalId: string, toLocalId: string) => Promise<void>
}): Promise<void> {
  /*
    Was schon wartet, wird nicht erneut angemeldet.

    Dieser Schritt läuft bei JEDEM Sync. Ohne die Prüfung stellte er für
    dasselbe Projekt bei jedem Zyklus einen weiteren Eintrag — am 07.09.2026
    waren es 523 offene `projects/create` für acht Projekte, in einer
    Warteschlange von knapp 10.000. Der Push legt zwar nichts doppelt an
    (`sync-push.ts` prüft die `cloudId`, bevor er anlegt), aber jeder Zyklus
    arbeitet sich durch den ganzen Berg.
  */
  const waiting = await db.syncQueue
    .filter(item => !item.synced && item.table === opts.tableName)
    .toArray()
  const alreadyQueued = new Set(
    waiting.map(item => item.data?.localId as string).filter(Boolean)
  )

  for (const rec of opts.unsynced) {
    const nameLc = rec.name.trim().toLowerCase()
    /*
      **Ein leerer Name ist kein Name.** Jedes neue Projekt entsteht mit
      `name: ''` — der Name kommt erst danach — und dieser Schritt läuft eine
      Sekunde später. Galt `''` als Name, traf das frische Projekt auf jedes
      andere nie benannte („Neues Projekt", irgendein Lebensbereich, lokal
      oder Cloud) und wurde als Duplikat hart gelöscht: Tab zu, „Übersicht"
      (gemeldet 2026-09-12). Zwei leere Namen sagen nichts über Gleichheit;
      die Deduplizierung gilt nur für echte Namen.
    */
    const localSyncedDup = nameLc
      ? opts.localSynced.find((r) => r.name.trim().toLowerCase() === nameLc)
      : undefined
    const isCloudDup = nameLc ? opts.cloudNames.has(nameLc) : false

    if (localSyncedDup || isCloudDup) {
      if (localSyncedDup) await opts.remapReferences(rec.id, localSyncedDup.id)
      await withSyncWrite(() => opts.table.delete(rec.id))
    } else if (!alreadyQueued.has(rec.id)) {
      // `silent`: Wir stehen VOR `getPendingChanges()` im selben Zyklus — dieser
      // Push nimmt den Eintrag ohnehin mit. Ohne die Ausnahme stieße jeder Sync
      // den nächsten an (ADR 0017).
      await db.queueSyncChange('create', opts.tableName, { localId: rec.id }, { silent: true })
    }
  }
}

/**
 * Perform full bidirectional sync.
 * Prefer using scheduleSync() for user-triggered actions.
 */
export async function performSync(options: SyncOptions = {}): Promise<SyncResult> {
  const result = createEmptySyncResult()

  // Offline-first gate: no network sync while HappyCloud-Sync is off. Read the
  // shared (Dexie-backed) storage mode so this holds across windows regardless
  // of IPC/React-state timing — every push/pull funnels through here. The web
  // adapter always returns 'cloud-sync', so web is unaffected.
  if ((await getPlatformAPI().getStorageMode()) === 'offline') {
    result.success = true
    return result
  }

  if (isSyncing) {
    result.errors.push(SYNC_IN_PROGRESS)
    return result
  }

  isSyncing = true
  try {
    const userId = await getCurrentUserId()
    if (!userId) {
      result.errors.push('No user ID - offline mode')
      return result
    }

    // Ownership guard: if any local record is owned by a different user,
    // Dexie is poisoned from an unclean account switch and we must wipe
    // BEFORE any sync operation runs — otherwise the reconciler could push
    // foreign records to the current user's cloud account.
    const ownership = await detectForeignLocalData(userId)
    if (ownership.hasForeignData) {
      console.warn(
        '[Sync] Foreign local data detected — wiping Dexie before sync',
        ownership.counts,
        'foreign users:',
        ownership.foreignUserIds,
      )
      Sentry.captureMessage('[Sync] Foreign data wipe triggered', {
        level: 'warning',
        extra: {
          counts: ownership.counts,
          foreignUserIds: ownership.foreignUserIds,
          currentUserId: userId,
        },
      })
      await wipeUserLocalState()
      // Skip this cycle entirely; the next regular sync will run against a
      // clean DB without any race conditions with the current in-flight cycle.
      return result
    }

    const tables = options.tables || ['tags', 'projects', 'projectComments', 'sections', 'tasks', 'subtasks', 'timeEntries', 'taskComments', 'taskReminders', 'canvasNotes', 'goals', 'habits', 'habitLogs', 'rewardUsages', 'userPreferences', 'settings']

    // 0. Stage local-only natural-key records (Tags, Projects) for upload, de-
    // duplicating by name against the cloud. See stageUnsyncedWithNameDedup.
    // Tasks/Sections/Goals/Habits have no natural key and ride the normal queue
    // (their CRUD already enqueues a create), so they need no staging step here.
    const allLocalTags = await db.tags.toArray()
    const unsyncedTags = allLocalTags.filter(t => !t.cloudId && !t.deletedAt && !t.synced)
    if (unsyncedTags.length > 0) {
      const cloudTags = await getCloudTags(userId)
      await stageUnsyncedWithNameDedup({
        table: db.tags,
        tableName: 'tags',
        unsynced: unsyncedTags,
        cloudNames: new Set(cloudTags.map(t => t.name.toLowerCase())),
        localSynced: allLocalTags.filter(t => !!t.cloudId && !t.deletedAt),
        remapReferences: async (fromId, toId) => {
          const tasksWithTag = await db.tasks.filter(t => t.tags.includes(fromId)).toArray()
          for (const task of tasksWithTag) {
            await withSyncWrite(() =>
              db.tasks.update(task.id, { tags: task.tags.map(id => (id === fromId ? toId : id)) }),
            )
          }
        },
      })
    }

    const allLocalProjects = await db.projects.toArray()
    const unsyncedProjects = allLocalProjects.filter(p => !p.cloudId && !p.deletedAt && !p.synced)
    if (unsyncedProjects.length > 0) {
      const cloudProjects = await getCloudProjects(userId)
      await stageUnsyncedWithNameDedup({
        table: db.projects,
        tableName: 'projects',
        unsynced: unsyncedProjects,
        cloudNames: new Set(cloudProjects.map(p => p.name.toLowerCase())),
        localSynced: allLocalProjects.filter(p => !!p.cloudId && !p.deletedAt),
        // Alles, was am Projekt hängt — nicht nur Aufgaben und Abschnitte (Diff-Audit 11.10.2026).
        remapReferences: remapMergedProject,
      })
    }

    /*
      0.5 **Waisen anmelden.** Der Kommentar bei Schritt 0 sagt, Tasks und die
      übrigen Tabellen „ride the normal queue (their CRUD already enqueues a
      create)" — das stimmt nur, solange jede Zeile durch eine CRUD-Methode
      entsteht. Ein Dexie-`modify` in einer Migration, ein Import oder ein
      abgebrochener Zyklus schreiben daran vorbei, und dann steht die Zeile für
      immer nur auf diesem Gerät: kein Warteschlangen-Eintrag, keine `cloudId`,
      und der Repair-Sweep im Pull greift nur bei Zeilen, die schon eine
      Cloud-Kennung haben.

      Läuft NACH dem Namens-Dedup oben: Was der dort schon angemeldet hat,
      überspringt der Sweep, und Tags/Projekte behalten ihren Dedup-Schutz.
    */
    await stageOrphanedLocalRecords()

    // Alte Unteraufgaben → Aufgaben (T14 #179), vor dem Lesen der Warteschlange: Die neuen
    // Zeilen gehen in DIESEM Zyklus hoch.
    await convertLegacySubtasks({ silent: true })

    const pendingChanges = await getPendingChanges()

    // 1. Push ALL pending local changes
    const pushResult = await pushLocalChanges(userId, pendingChanges)
    result.synced.created += pushResult.created
    result.synced.updated += pushResult.updated
    result.synced.deleted += pushResult.deleted
    result.errors.push(...pushResult.errors)
    if (pushResult.created || pushResult.updated || pushResult.deleted || pushResult.errors.length) {
      console.log(`[Sync] PUSH result — created: ${pushResult.created}, updated: ${pushResult.updated}, deleted: ${pushResult.deleted}, errors: ${pushResult.errors.length}`)
    }

    /*
      **Vorprüfung** — eine RPC für alle zwölf Tabellen, vor den Pulls.

      Liefert je Tabelle Zählstand und jüngsten Zeitstempel. Wo sich an beidem
      nichts geändert hat, überspringt `pullRemoteEntities` die Tabelle ganz:
      kein Holen, kein Holen der Löschungen, keine Abstimmung, nicht einmal ein
      Lesen der lokalen Tabelle. Ein Leerlauf-Sync kostet damit diese eine
      Anfrage statt 27 (Messung 20.09.2026: 28,6k Anfragen/Tag bei einem Nutzer).

      `null` (alter Server ohne `get_sync_state`, Netzfehler) heißt: volle Runde
      wie bisher.
    */
    const cloudState = await getCloudSyncState(userId)

    // 2. Pull remote changes per table.
    // After a pull that wrote rows we `yieldToEventLoop()` once, so React +
    // Dexie LiveQuery callbacks queued during it get to run before the next
    // one starts (each pull holds the IDB transaction and the JS thread for
    // hundreds of ms when there are many rows). Without the yield, a sync that
    // lands during a click chain stalls the next user action by the entire
    // serialised pull duration.
    // Only then, not after every table (#305): each yield is a `setTimeout(0)`,
    // which Chromium throttles to about one per second in a hidden window —
    // an idle cycle used to wait 224 times and ran for minutes in the tray.
    for (const table of tables) {
      const pulledBefore = result.synced.created + result.synced.updated + result.synced.deleted
      if (table === 'tags') {
        const r = await pullRemoteEntities(userId, tagPullDescriptor, pendingChanges, cloudState?.tag ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      if (table === 'tasks') {
        const r = await pullRemoteEntities(userId, taskPullDescriptor, pendingChanges, cloudState?.task ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
        if (r.created || r.updated || r.errors.length) {
          console.log(`[Sync] PULL tasks — created: ${r.created}, updated: ${r.updated}, errors: ${r.errors.length}`)
        }
      }
      // After tasks on purpose: buildLocalInsert bails out when the parent task
      // is not on this device yet, so pulling them in this order means a fresh
      // device gets a task and its checklist in the same cycle.
      if (table === 'subtasks') {
        const r = await pullRemoteEntities(userId, subtaskPullDescriptor, pendingChanges, cloudState?.subtask ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
        // Ein Gerät mit älterem Stand schreibt weiter in die alte Tabelle — was es
        // brachte, wird hier gleich zur Aufgabe (T14 #179) und geht im nächsten Zyklus hoch.
        if (r.created || r.updated) await convertLegacySubtasks()
      }
      // Auch nach tasks, aus demselben Grund wie subtasks: `buildLocalInsert`
      // steigt aus, wenn die Aufgabe des Abschnitts noch nicht hier liegt.
      if (table === 'timeEntries') {
        const r = await pullRemoteEntities(userId, timeEntryPullDescriptor, pendingChanges, cloudState?.timeEntry ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Nach tasks, aus demselben Grund: Ein Kommentar ohne seine Aufgabe wartet (#182).
      if (table === 'taskComments') {
        const r = await pullRemoteEntities(userId, taskCommentPullDescriptor, pendingChanges, cloudState?.taskComment ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Ebenso die Erinnerungen: Eine ohne ihre Aufgabe wartet auf den nächsten Zyklus.
      if (table === 'taskReminders') {
        const r = await pullRemoteEntities(userId, taskReminderPullDescriptor, pendingChanges, cloudState?.taskReminder ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      if (table === 'projects') {
        const r = await pullRemoteEntities(userId, projectPullDescriptor, pendingChanges, cloudState?.project ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Nach projects: Ein Kommentar ohne sein Projekt wartet (#184).
      if (table === 'projectComments') {
        const r = await pullRemoteEntities(userId, projectCommentPullDescriptor, pendingChanges, cloudState?.projectComment ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      if (table === 'sections') {
        const r = await pullRemoteEntities(userId, sectionPullDescriptor, pendingChanges, cloudState?.section ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Nach projects UND nach tasks: `buildLocalInsert` steigt aus, wenn das
      // Projekt noch nicht auf diesem Gerät ist — und die **Verbindung** eines
      // übernommenen Notiz lässt sich nur auflösen, wenn seine Aufgabe
      // schon lokal liegt. Beides bekommt ein frisches Gerät damit im selben
      // Zyklus.
      if (table === 'canvasNotes') {
        const r = await pullRemoteEntities(userId, canvasNotePullDescriptor, pendingChanges, cloudState?.canvasNote ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      if (table === 'goals') {
        const r = await pullRemoteEntities(userId, goalPullDescriptor, pendingChanges, cloudState?.goal ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Habits before habit_logs: logs map to a local habit that must exist first.
      if (table === 'habits') {
        const r = await pullRemoteEntities(userId, habitPullDescriptor, pendingChanges, cloudState?.habit ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      if (table === 'habitLogs') {
        const r = await pullRemoteEntities(userId, habitLogPullDescriptor, pendingChanges, cloudState?.habitLog ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Standalone: reward usages reference the bundled catalogue, not a synced row.
      if (table === 'rewardUsages') {
        const r = await pullRemoteEntities(userId, rewardUsagePullDescriptor, pendingChanges, cloudState?.rewardUsage ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // Standalone: preferences are keyed by their own name, no FK to resolve.
      if (table === 'userPreferences') {
        const r = await pullRemoteEntities(userId, userPreferencePullDescriptor, pendingChanges, cloudState?.preference ?? null)
        result.synced.created += r.created
        result.synced.updated += r.updated
        result.synced.deleted += r.deleted
        result.conflicts.push(...r.conflicts)
        result.errors.push(...r.errors)
      }
      // The legacy `settings` table stays device-local on purpose — see the
      // userPreferences block in indexeddb.ts.

      if (result.synced.created + result.synced.updated + result.synced.deleted > pulledBefore) {
        await yieldToEventLoop()
      }
    }

    // 3. Post-sync maintenance
    const repairResult = await repairOrphanedTaskAssociations(userId)
    result.synced.updated += repairResult.repaired

    const purgedTags = await purgeOrphanedCloudTags(userId)
    result.synced.deleted += purgedTags

    await clearSyncedItems(7)
    await purgeSyncedDeletions()

    result.success = result.errors.length === 0

    /*
      Den Lauf festhalten — aber nur einen fehlerfreien.

      „Zuletzt synchronisiert" in den Einstellungen las bislang die
      `lastXSyncTime`-Schlüssel, und die tragen den höchsten `updated_at` der
      geholten Cloud-Zeilen, nicht die Uhrzeit des Laufs. Am 07.09.2026 stand
      dort „vor 2 Minuten", während der Push seit Stunden nichts hochbekam.

      Bei Fehlern bleibt der alte Stand stehen: Die Anzeige soll dann altern,
      denn genau das ist die ehrliche Auskunft.
    */
    if (result.success) await markSyncRun()

    const { created, updated, deleted } = result.synced
    if (created || updated || deleted || result.errors.length) {
      console.log(`[Sync] done — created: ${created}, updated: ${updated}, deleted: ${deleted}${result.errors.length ? `, errors: ${result.errors.length}` : ''}`)
    }

    // Report sync errors to PostHog for production monitoring
    if (result.errors.length > 0) {
      const hasSchemaError = result.errors.some(e => e.includes('[SCHEMA]'))
      trackEvent(ANALYTICS_EVENTS.SYNC_ERROR, {
        error_count: result.errors.length,
        error_samples: result.errors.slice(0, 3),
        has_schema_error: hasSchemaError,
      })
      // Schema errors mean local DB is behind cloud schema — devs must
      // ship a Dexie version bump. Surface as Sentry error (not just a
      // PostHog metric) so it triggers an alert.
      if (hasSchemaError) {
        Sentry.captureMessage('[Sync] Schema error during sync — DB upgrade needed', {
          level: 'error',
          tags: { source: 'sync-engine', phase: 'performSync', kind: 'schema' },
          extra: { error_samples: result.errors.slice(0, 5) },
        })
      }
    }

    // Notify listeners (e.g. useTabManager) that sync is complete
    if (result.success) {
      window.dispatchEvent(new CustomEvent('sync:complete'))
    }

    return result
  } catch (error) {
    console.error('[Sync] performSync error:', error)
    result.errors.push(`Sync failed: ${error}`)
    trackEvent(ANALYTICS_EVENTS.SYNC_ERROR, {
      error_count: 1,
      error_samples: [`Sync failed: ${error}`],
      has_schema_error: false,
    })
    Sentry.captureException(error, {
      tags: { source: 'sync-engine', phase: 'performSync' },
    })
    return result
  } finally {
    isSyncing = false
  }
}

/**
 * Push local changes to cloud
 */
async function pushLocalChanges(
  userId: string,
  pendingChanges: SyncQueueItem[],
): Promise<{ created: number; updated: number; deleted: number; errors: string[] }> {
  const result = { created: 0, updated: 0, deleted: 0, errors: [] as string[] }

  try {
    // Defense-in-depth: drop any pending change whose local entity belongs
    // to a different user. Under normal conditions the engine-level
    // ownership guard catches this earlier, but this prevents a leak if
    // the guard is ever bypassed or a race condition occurs.
    const ownedChanges: SyncQueueItem[] = []
    for (const change of pendingChanges) {
      const localId = change.data?.localId
      if (!localId) {
        ownedChanges.push(change)
        continue
      }
      const table = (db as unknown as Record<string, { get: (id: string) => Promise<{ userId?: string | null } | undefined> }>)[change.table]
      if (!table?.get) {
        ownedChanges.push(change)
        continue
      }
      const local = await table.get(localId)
      if (local && local.userId != null && local.userId !== userId) {
        console.warn(
          `[Sync] Dropping push for foreign-owned ${change.table} ${localId} (owner: ${local.userId}, current: ${userId})`,
        )
        await markAsFailed(change.id!, `Foreign-owned record rejected by sync-push filter`)
        continue
      }
      ownedChanges.push(change)
    }

    // Sort: tags first, then projects, sections, tasks — so IDs can be resolved.
    // Goals are standalone (no FK dependency) so their order is irrelevant.
    //
    // `canvasNotes` liegt NACH `tasks`, obwohl es nur an `projects` hängt: Ein
    // gerade **übernommener** Notiz trägt eine `taskId`, und die muss beim
    // Push zur Cloud-Kennung aufgelöst werden. Stünde er vorn, hätte die
    // frisch angelegte Aufgabe noch keine — die Verbindung ginge verloren oder
    // die Notiz bliebe einen Zyklus liegen.
    const tableOrder: Record<string, number> = { tags: 0, projects: 1, sections: 2, tasks: 3, subtasks: 4, timeEntries: 4.2, taskComments: 4.3, taskReminders: 4.35, projectComments: 4.4, canvasNotes: 4.5, goals: 5, habits: 6, habitLogs: 7, rewardUsages: 8, userPreferences: 9 }
    const sorted = ownedChanges.toSorted((a, b) =>
      (tableOrder[a.table] ?? 10) - (tableOrder[b.table] ?? 10)
    )

    // `PushDescriptor<any>`, not `typeof taskPushDescriptor`: the map holds one
    // descriptor per entity, and each is parameterised on its own row type.
    const descriptors: Record<string, PushDescriptor<any>> = {
      tags: tagPushDescriptor,
      tasks: taskPushDescriptor,
      subtasks: subtaskPushDescriptor,
      timeEntries: timeEntryPushDescriptor,
      taskComments: taskCommentPushDescriptor,
      taskReminders: taskReminderPushDescriptor,
      projectComments: projectCommentPushDescriptor,
      projects: projectPushDescriptor,
      sections: sectionPushDescriptor,
      goals: goalPushDescriptor,
      habits: habitPushDescriptor,
      habitLogs: habitLogPushDescriptor,
      rewardUsages: rewardUsagePushDescriptor,
      userPreferences: userPreferencePushDescriptor,
      canvasNotes: canvasNotePushDescriptor,
    }

    for (const change of sorted) {
      try {
        const descriptor = descriptors[change.table]
        if (descriptor) {
          await pushEntityChange(change, userId, result, descriptor)
        }
      } catch (error) {
        result.errors.push(`Failed to sync change ${change.id}: ${error}`)
        await markAsFailed(change.id!, String(error))
      }
    }

    return result
  } catch (error) {
    result.errors.push(`Push failed: ${error}`)
    return result
  }
}

